import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { format, parseISO } from 'date-fns';
import orderBy from 'lodash/orderBy';
import { XCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Actions as AGREEMENTS } from '../../store/actions/agreementsActions';
import { selectCancelEligibility } from '../../store/selectors/agreementsSelectors';
import { toggleAlertMessage } from '../../store/action';
import { Actions as PAYMENT } from '../../store/actions/paymentActions';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from '../ui/dialog';

const CancelSubscriptionButton = ({
  getMyAgreements,
  requestSubscriptionCancel,
  updateStripeSubscriptions,
  cancelEligibility,
  stripeCustomerId,
  subscriptionIds,
  subLabel = 'main subscription',
  onSuccess,
  showMessage,
  disabled,
  variant = 'outline',
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (stripeCustomerId) getMyAgreements();
  }, [stripeCustomerId, getMyAgreements]);

  const mode = cancelEligibility?.mode || 'self_serve';
  const comm = cancelEligibility?.commitmentEndDate;
  const email = cancelEligibility?.contactEmail;

  if (mode === 'contact_support') {
    return (
      <Card className="border-warning/30 bg-warning-bg/10 max-w-md">
        <CardHeader>
          <CardTitle className="text-base">Cancellation</CardTitle>
          <CardDescription>
            Your committed term{comm ? ` ends on ${format(parseISO(comm), 'PPP')}.` : '.'} To discuss cancellation, please{' '}
            {email ? (
              <a className="text-primary underline" href={'mailto:' + email}>
                {email}
              </a>
            ) : (
              ' contact support'
            )}
            .
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const onConfirm = async () => {
    if (!subscriptionIds || !subscriptionIds.length) {
      showMessage(true, 'No active subscription to cancel', 'error', 3000);
      return;
    }
    setBusy(true);
    const r = await requestSubscriptionCancel({ confirm: true, reason: 'user_requested' });
    if (r?.blocked) {
      showMessage(
        true,
        r.error?.code || r.error?.message || 'Cannot cancel at this time',
        'error',
        5000
      );
      setBusy(false);
      setConfirmOpen(false);
      return;
    }
    if (!r?.ok) {
      showMessage(true, (r && r.error) || 'Request failed', 'error', 5000);
      setBusy(false);
      setConfirmOpen(false);
      return;
    }
    try {
      await updateStripeSubscriptions(subscriptionIds, 'cancel_at_period_end', 'true');
      showMessage(
        true,
        r.data?.message ||
          (mode === 'self_serve_at_term_end' ? 'Subscription will end on term date' : 'Cancellation scheduled'),
        'success',
        5000
      );
      if (onSuccess) onSuccess();
    } catch (e) {
      showMessage(true, e.message || 'Stripe update failed', 'error', 5000);
    }
    setBusy(false);
    setConfirmOpen(false);
  };

  const showBanner = mode === 'self_serve_at_term_end' && comm;

  return (
    <div>
      {showBanner && (
        <p className="text-sm text-warning mb-2">
          Your subscription will not renew; it ends on {format(parseISO(comm), 'PPP')}.
        </p>
      )}
      <Button
        type="button"
        variant={variant}
        onClick={() => setConfirmOpen(true)}
        disabled={disabled || !stripeCustomerId || !subscriptionIds?.length}
        loading={busy}
      >
        <XCircle size={16} strokeWidth={1.5} className="mr-1" />
        Cancel {subLabel}
      </Button>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel {subLabel}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            {mode === 'self_serve_at_term_end' && comm && (
              <p className="text-sm text-foreground">
                This will set your plan to not renew. Access continues until {format(parseISO(comm), 'PPP')}.
              </p>
            )}
            {mode === 'self_serve' && (
              <p className="text-sm text-foreground">
                Your subscription will be cancelled at the end of the current billing period. Continue?
              </p>
            )}
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              Keep
            </Button>
            <Button variant="destructive" onClick={onConfirm} loading={busy}>
              Confirm cancellation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const getSubscriptionIds = (state) => {
  const subs = state.payment?.stripe?.stripeSubscriptions;
  if (!Array.isArray(subs) || subs.length === 0) return null;
  const last = orderBy(subs, (s) => s.created, 'desc')?.[0];
  return last ? [last.id] : null;
};

const mapState = (state) => ({
  cancelEligibility: selectCancelEligibility(state),
  stripeCustomerId: state.payment?.stripe?.stripeCustomerId,
  subscriptionIds: getSubscriptionIds(state),
});

const mapDispatch = (d) => ({
  getMyAgreements: (oid) => d(AGREEMENTS.getMyAgreements(oid)),
  requestSubscriptionCancel: (o) => d(AGREEMENTS.requestSubscriptionCancel(o)),
  updateStripeSubscriptions: (a, f, v) => d(PAYMENT.updateStripeSubscriptions(a, f, v)),
  showMessage: (...a) => d(toggleAlertMessage(...a)),
});

export default connect(mapState, mapDispatch)(CancelSubscriptionButton);
